import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

const MONTHS_NL = ['Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni', 'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December'];
const DAYS_SHORT = ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'];

const DAY_MAP = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const PERIOD_DEFAULTS = {
  morning_start: '09:00', morning_end: '12:00',
  afternoon_start: '13:00', afternoon_end: '17:00',
  evening_start: '17:00', evening_end: '20:00',
};

function generatePeriodSlots(startStr, endStr, durationMinutes) {
  const toMins = (t) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
  const slots = [];
  let cur = toMins(startStr);
  const end = toMins(endStr);
  while (cur + durationMinutes <= end) {
    slots.push(`${String(Math.floor(cur / 60)).padStart(2, '0')}:${String(cur % 60).padStart(2, '0')}`);
    cur += durationMinutes;
  }
  return slots;
}

function getAvailableSlots(date, avail, bookedSlots, blackouts) {
  if (!avail || !date) return [];
  const dateStr = date.toISOString().split('T')[0];
  if (blackouts.includes(dateStr)) return [];

  const dayName = DAY_MAP[date.getDay()];
  const slotDuration = avail.slot_duration || 60;

  const slots = [];
  for (const period of ['morning', 'afternoon', 'evening']) {
    if (avail[`${dayName}_${period}`]) {
      const start = avail[`${period}_start`] || PERIOD_DEFAULTS[`${period}_start`];
      const end   = avail[`${period}_end`]   || PERIOD_DEFAULTS[`${period}_end`];
      slots.push(...generatePeriodSlots(start, end, slotDuration));
    }
  }

  const booked = bookedSlots.filter(b => b.appointment_date === dateStr).map(b => b.appointment_time);
  return slots.filter(s => !booked.includes(s));
}

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

export default function PublicBooking() {
  const { agentSlug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [step, setStep] = useState(1);
  const [apptType, setApptType] = useState('Video call');
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [calMonth, setCalMonth] = useState(new Date());
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', heard_from: '', trip_interest: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/book/${agentSlug}`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => { setNotFound(true); setLoading(false); });
  }, [agentSlug]);

  const getDaysInMonth = () => {
    const year = calMonth.getFullYear();
    const month = calMonth.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const startOffset = (firstDay + 6) % 7; // Monday-first
    return { daysInMonth, startOffset };
  };

  const isDateAvailable = (day) => {
    if (!data) return false;
    const date = new Date(calMonth.getFullYear(), calMonth.getMonth(), day);
    if (date < new Date(new Date().toDateString())) return false; // past
    const slots = getAvailableSlots(date, data.availability, data.booked_slots, data.blackout_dates);
    return slots.length > 0;
  };

  const handleDateClick = (day) => {
    const date = new Date(calMonth.getFullYear(), calMonth.getMonth(), day);
    setSelectedDate(date);
    setSelectedTime('');
    setStep(3);
  };

  const availableSlots = selectedDate
    ? getAvailableSlots(selectedDate, data?.availability, data?.booked_slots || [], data?.blackout_dates || [])
    : [];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/book/${agentSlug}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          appointment_type: apptType,
          appointment_date: selectedDate?.toISOString().split('T')[0],
          appointment_time: selectedTime,
        }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Verzenden mislukt');
      setSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#f4f6f9' }}>
      <div className="spinner spinner-blue" style={{ width: 36, height: 36, borderWidth: 3 }} />
    </div>
  );

  if (notFound) return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#f4f6f9' }}>
      <div className="text-center">
        <div className="text-5xl mb-4">🔍</div>
        <h1 className="text-xl font-bold text-gray-900">Reisagent niet gevonden</h1>
        <p className="text-gray-500 mt-2">Deze boekingspagina bestaat niet.</p>
      </div>
    </div>
  );

  if (submitted) return (
    <div className="min-h-screen flex items-center justify-center p-8" style={{ backgroundColor: '#f4f6f9' }}>
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-10 text-center max-w-md">
        <div className="text-5xl mb-4">✅</div>
        <h1 className="text-xl font-bold text-gray-900 mb-2">
          Bedankt, {form.first_name}!
        </h1>
        <p className="text-gray-600">
          Uw afspraakverzoek is ontvangen. {data?.agent?.agent_name} neemt binnenkort contact met u op ter bevestiging.
        </p>
        <div className="mt-6 p-4 bg-blue-50 rounded-xl text-left text-sm text-gray-700">
          <p><strong>Datum:</strong> {selectedDate?.toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
          <p className="mt-1"><strong>Tijd:</strong> {selectedTime}</p>
          <p className="mt-1"><strong>Type:</strong> {apptType}</p>
        </div>
      </div>
    </div>
  );

  const { daysInMonth, startOffset } = getDaysInMonth();
  const agent = data?.agent;

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f4f6f9' }}>
      {/* Header */}
      <div className="text-white py-10 px-6 text-center" style={{ backgroundColor: 'var(--brand)' }}>
        <div className="max-w-xl mx-auto">
          <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4 text-3xl">✈️</div>
          <h1 className="text-2xl font-bold">Plan een afspraak</h1>
          <p className="text-blue-100 mt-1 text-lg">met {agent?.agent_name}</p>
          <p className="font-semibold text-white mt-0.5">{agent?.agency_name}</p>
          {agent?.agent_bio && (
            <p className="text-blue-200 mt-3 text-sm max-w-md mx-auto">{agent.agent_bio}</p>
          )}
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-8">
        {/* Steps indicator */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3, 4].map(s => (
            <React.Fragment key={s}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${s <= step ? 'text-white' : 'bg-gray-200 text-gray-400'}`}
                style={s <= step ? { backgroundColor: 'var(--brand)' } : {}}>
                {s}
              </div>
              {s < 4 && <div className={`h-0.5 w-12 transition-all ${s < step ? '' : 'bg-gray-200'}`}
                style={s < step ? { backgroundColor: 'var(--brand)' } : {}} />}
            </React.Fragment>
          ))}
        </div>

        {/* Step 1: Type */}
        <div className="card mb-4">
          <h2 className="font-semibold text-gray-900 mb-4">Stap 1: Type afspraak</h2>
          <div className="grid grid-cols-2 gap-3">
            {['Video call', 'In-person'].map(type => (
              <button key={type} onClick={() => { setApptType(type); setStep(Math.max(step, 2)); }}
                className={`p-4 rounded-xl border-2 transition-all text-left ${apptType === type ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}`}
                style={apptType === type ? { borderColor: 'var(--brand)' } : {}}>
                <div className="text-2xl mb-2">{type === 'Video call' ? '📹' : '🏢'}</div>
                <div className="font-medium text-gray-900 text-sm">{type}</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {type === 'Video call' ? 'Online videogesprek' : `Kantoor: ${agent?.office_address || 'Ons kantoor'}`}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Step 2: Calendar */}
        {step >= 2 && (
          <div className="card mb-4">
            <h2 className="font-semibold text-gray-900 mb-4">Stap 2: Kies een datum</h2>
            <div className="flex items-center justify-between mb-4">
              <button onClick={() => { const d = new Date(calMonth); d.setMonth(d.getMonth() - 1); setCalMonth(d); }}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-600">‹</button>
              <span className="font-semibold text-gray-900">
                {MONTHS_NL[calMonth.getMonth()]} {calMonth.getFullYear()}
              </span>
              <button onClick={() => { const d = new Date(calMonth); d.setMonth(d.getMonth() + 1); setCalMonth(d); }}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-600">›</button>
            </div>
            <div className="grid grid-cols-7 gap-1 mb-2">
              {DAYS_SHORT.map(d => <div key={d} className="text-center text-xs font-medium text-gray-400 py-1">{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {Array.from({ length: startOffset }).map((_, i) => <div key={i} />)}
              {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
                const available = isDateAvailable(day);
                const isSelected = selectedDate?.getDate() === day &&
                  selectedDate?.getMonth() === calMonth.getMonth() &&
                  selectedDate?.getFullYear() === calMonth.getFullYear();
                const isPast = new Date(calMonth.getFullYear(), calMonth.getMonth(), day) < new Date(new Date().toDateString());

                return (
                  <button key={day}
                    onClick={() => available && handleDateClick(day)}
                    disabled={!available || isPast}
                    className={`w-full aspect-square rounded-lg text-sm font-medium transition-all ${
                      isSelected ? 'text-white' :
                      available ? 'hover:bg-blue-50 text-gray-900' :
                      isPast ? 'text-gray-300 cursor-not-allowed' : 'text-gray-300 cursor-not-allowed'
                    }`}
                    style={isSelected ? { backgroundColor: 'var(--brand)' } : available ? { backgroundColor: 'var(--brand-light)' } : {}}>
                    {day}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Step 3: Time slots */}
        {step >= 3 && selectedDate && (
          <div className="card mb-4">
            <h2 className="font-semibold text-gray-900 mb-1">Stap 3: Kies een tijdstip</h2>
            <p className="text-sm text-gray-500 mb-4">
              {selectedDate.toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' })}
            </p>
            {availableSlots.length === 0 ? (
              <p className="text-gray-400 text-sm">Geen beschikbare tijdslots op deze dag.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {availableSlots.map(slot => (
                  <button key={slot}
                    onClick={() => { setSelectedTime(slot); setStep(4); }}
                    className={`px-4 py-2 rounded-lg text-sm font-medium border-2 transition-all ${
                      selectedTime === slot ? 'text-white border-transparent' : 'border-gray-200 text-gray-700 hover:border-blue-400'
                    }`}
                    style={selectedTime === slot ? { backgroundColor: 'var(--brand)' } : {}}>
                    {slot}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 4: Details form */}
        {step >= 4 && selectedTime && (
          <div className="card">
            <h2 className="font-semibold text-gray-900 mb-4">Stap 4: Uw gegevens</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Voornaam *</label>
                  <input required className="input" value={form.first_name}
                    onChange={e => setForm({ ...form, first_name: e.target.value })} />
                </div>
                <div>
                  <label className="label">Achternaam *</label>
                  <input required className="input" value={form.last_name}
                    onChange={e => setForm({ ...form, last_name: e.target.value })} />
                </div>
              </div>
              <div>
                <label className="label">E-mailadres *</label>
                <input type="email" required className="input" value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <label className="label">Telefoonnummer</label>
                <input type="tel" className="input" value={form.phone}
                  onChange={e => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div>
                <label className="label">Waar bent u in geïnteresseerd?</label>
                <input className="input" value={form.trip_interest} placeholder="Bijv. zonvakantie, stedentrip, verre reis..."
                  onChange={e => setForm({ ...form, trip_interest: e.target.value })} />
              </div>
              <div>
                <label className="label">Hoe heeft u ons gevonden?</label>
                <select className="input" value={form.heard_from}
                  onChange={e => setForm({ ...form, heard_from: e.target.value })}>
                  <option value="">Kies een optie</option>
                  {['Google', 'Social media', 'Doorverwijzing', 'Anders'].map(o => <option key={o}>{o}</option>)}
                </select>
              </div>

              {/* Summary */}
              <div className="p-4 rounded-xl border border-blue-200" style={{ backgroundColor: 'var(--brand-light)' }}>
                <p className="text-sm font-semibold text-gray-900 mb-2">Overzicht afspraak</p>
                <div className="text-sm text-gray-700 space-y-1">
                  <p>📅 {selectedDate?.toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' })} om {selectedTime}</p>
                  <p>{apptType === 'Video call' ? '📹' : '🏢'} {apptType}</p>
                  <p>👤 {agent?.agent_name} — {agent?.agency_name}</p>
                </div>
              </div>

              {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}

              <button type="submit" disabled={submitting} className="btn-primary w-full justify-center py-3 text-base">
                {submitting ? <span className="spinner" /> : null}
                {submitting ? 'Verzenden...' : 'Afspraak bevestigen'}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
